import React from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Provider as BusProvider } from 'react-bus';
import _get from 'lodash/get';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import RedBox from 'redbox-react';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-material.css';
import ReactDOM from 'react-dom/client';

import createStore from './store/createStore';
import './styles/layout.scss';
import { buildMuiTheme } from 'src/muiTheme';
import { SplitSdk, SplitFactory } from '@splitsoftware/splitio-react';
import queryClient from './queryClient';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClientProvider } from '@tanstack/react-query';
import { StacklineThemeProvider } from '@stackline/ui';
import RouterPropsProvider from './providers/RouterPropsProvider';
import { LicenseInfo } from '@mui/x-license-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import ExportContextProvider from 'src/providers/ExportContextProvider';
import SnackbarProvider from './providers/SnackbarProvider';
import PopupProvider from 'src/providers/PopupProvider';
import UserProfileImageProvider from './providers/UserProfileImageProvider';
import NotificationProvider from 'src/providers/NotificationProvider';
import { BulkAdjustmentProvider } from './components/BeaconV2Components/ExperimentalLayout/Forecasts/BulkAdjustments/BulkAdjustmentProvider';
import { PollingContextProvider } from 'src/providers/PollingContextProvider';
import AppModals from 'src/components/AddModals';
import UserTrackingInitializer from './components/UserTrackingInitializer';
import { useKonamiRaveMode } from './hooks/useKonamiCode';
import { isV2Enabled, isV2SupportedApp } from 'src/utils/authVersion';
import { getAppName } from 'src/utils/app';

const AppV1 = React.lazy(() => import('./components/App/App').then(m => ({ default: m.default as any })));
const AppV2 = React.lazy(() => import('./components/App/App_v2').then(m => ({ default: m.default as any })));

// Component that activates Konami code "rave mode" easter egg
function KonamiCodeHandler() {
  useKonamiRaveMode();
  return null;
}

function AppSwitch() {
  // Lock the V1/V2 shell decision at mount. Re-evaluating isV2Enabled() on
  // every render causes AppV2 → AppV1 swaps mid-session (e.g. when operations_v2
  // fetchConfig falls back to V1 and writes sl-auth-method=v1), which
  // re-runs both shells' constructors. That double-fires non-idempotent
  // side effects (numeral formatters, mixpanel/sentry/logrocket gating,
  // axios config, Amplify hub listeners) and crashes the page.
  const [useV2Flow] = React.useState(() => {
    const isEnterpriseSso = window.location.hostname.includes('enterprise-sso');
    const v2 = isV2Enabled();
    const appName = getAppName();
    return isEnterpriseSso || (v2 && isV2SupportedApp(appName));
  });
  const AppComponent = useV2Flow ? AppV2 : AppV1;

  const inner = (
    <React.Suspense fallback={null}>
      <>
        {(() => {
          const { ContentGenerationsService } = require('src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useAllGenerationsService');
          return <ContentGenerationsService />;
        })()}
        <KonamiCodeHandler />
        <AppModals />
        <AppComponent />
      </>
    </React.Suspense>
  );

  return inner;
}

// ========================================================
// A/B Test Provider Instantiation (Split.io)
// ========================================================
export const factory = SplitSdk({
  core: {
    authorizationKey: 'fm4ur66cflc3lt0jjrh6j3olsu0iebp4ji1j',
    key: 'key'
  },
  startup: {
    requestTimeoutBeforeReady: 5,
    readyTimeout: 5
  },
  debug: __DEV__ ? 'WARN' : 'ERROR'
});

// ========================================================
// Store Instantiation
// ========================================================
export const store = createStore();

store.subscribe(() => {
  store.getState();
});

// ========================================================
// MUI X License
// ========================================================
LicenseInfo.setLicenseKey(
  'cf5cbed7d3487f7270e48f115ee63a45T1JERVI6Mzk5MjEsRVhQSVJZPTE2NzkxNzQyMzcwMDAsS0VZVkVSU0lPTj0x'
);

// ========================================================
// Render Setup
// ========================================================
const MOUNT_NODE = document.getElementById('root');

// Create root instance
const root = ReactDOM.createRoot(MOUNT_NODE);

let render = () => {
  root.render(
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          <BusProvider>
            <SplitFactory factory={factory}>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={buildMuiTheme()}>
                  <StacklineThemeProvider>
                    <BrowserRouter basename="/">
                      <NotificationProvider>
                        <PopupProvider>
                          <SnackbarProvider>
                            <RouterPropsProvider>
                              <ExportContextProvider>
                                <UserProfileImageProvider>
                                  <PollingContextProvider>
                                    <BulkAdjustmentProvider>
                                      <AppSwitch />
                                    </BulkAdjustmentProvider>
                                  </PollingContextProvider>
                                </UserProfileImageProvider>
                              </ExportContextProvider>
                            </RouterPropsProvider>
                          </SnackbarProvider>
                        </PopupProvider>
                      </NotificationProvider>
                    </BrowserRouter>
                  </StacklineThemeProvider>
                </ThemeProvider>
              </StyledEngineProvider>
            </SplitFactory>
          </BusProvider>
        </QueryClientProvider>
      </Provider>
    </LocalizationProvider>
  );
};

// This code is excluded from production bundle
if (__DEV__) {
  if (module.hot) {
    // Development render functions
    const renderApp = render;
    const renderError = (error) => {
      root.render(<RedBox error={error} />);
    };

    // Wrap render in try/catch
    render = () => {
      try {
        renderApp();
      } catch (error) {
        renderError(error);
      }
    };

    // Update the hot module replacement setup
    module.hot.accept([], () => {
      // Re-render using the root instance
      render();
    });
  }
}

if (store.getState().app.stage === 'dev') {
  // Enable these on dev and localhost
  // Make the Redux store available as a global variable if in debug mode
  (window as any).store = store;
  (window as any).getState = () => store.getState();
  (window as any).ess = () => store.getState().entitySearchService;
  (window as any).getMainEntity = () => _get(store.getState(), ['entityService', 'mainEntity']);
  (window as any).queryClient = queryClient;
  (window as any).DEBUG_MODE = false;
  (window as any).enableLogging = () => {
    (window as any).DEBUG_MODE = true;
    // Update Logger's debug mode
    import('./services/userTracking/utils/Logger').then(({ Logger }) => {
      Logger.getInstance().setDebugMode(true);
    });
  };
  (window as any).disableLogging = () => {
    (window as any).DEBUG_MODE = false;
    // Update Logger's debug mode
    import('./services/userTracking/utils/Logger').then(({ Logger }) => {
      Logger.getInstance().setDebugMode(false);
    });
  };
}

// ========================================================
// Go!
// ========================================================
if (process.env.NODE_ENV !== 'test') {
  render();
}
